import { keepPreviousData, useQueries, useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { api } from "../api";
import {
  changeText,
  compact,
  percent,
  statusItems,
  totals,
  weekChange,
  type AnalyticsMetric,
  type AnalyticsOverview,
  type AnalyticsSeries,
} from "../analytics";
import { Sparkline } from "../components/Sparkline";
import { StatusStrip } from "../components/StatusStrip";
import { useMe, useRole } from "../components/Shell";
import { dayLabel } from "../earlyAccess";
import { roleLabel } from "../nav";
import { errorText, unwrap } from "../problem";

// The tiles' trends: the 30 days ending with the latest built day.
const TRENDS = {
  signups: "Sign-ups per day",
  active: "Daily active users",
  active_7d: "Weekly active users",
  active_30d: "Monthly active users",
  circles: "Circles (when built)",
  early_access_signups: "Early access sign-ups per day",
} as const satisfies Partial<Record<AnalyticsMetric, string>>;
type Trend = keyof typeof TRENDS;
const TREND_METRICS = Object.keys(TRENDS) as Trend[];

export function OverviewPage() {
  const role = useRole();
  const me = useMe();
  const overview = useQuery({
    queryKey: ["analytics-overview"],
    queryFn: async () => unwrap(await api.GET("/analytics/overview")),
    // Online now and the status strip move; the rest changes nightly.
    refetchInterval: 60_000,
  });
  // Keyed by the latest built day, so the trends move on when the polled overview sees a new build.
  const built = overview.data?.status.analytics_built_through;
  const trends = useQueries({
    queries: TREND_METRICS.map((metric) => ({
      queryKey: ["analytics-series", metric, built],
      queryFn: async () => unwrap(await api.GET("/analytics/series", { params: { query: { metric } } })),
      enabled: overview.isSuccess,
      placeholderData: keepPreviousData,
      staleTime: 10 * 60_000,
    })),
  });
  const series = Object.fromEntries(TREND_METRICS.map((metric, i) => [metric, trends[i]!.data])) as Record<
    Trend,
    AnalyticsSeries | undefined
  >;
  const trendError = trends.find((query) => query.isError)?.error;
  return (
    <>
      <h1>Overview</h1>
      {me.data && (
        <p className="muted">
          Signed in as {me.data.name || me.data.email}, {roleLabel(role).toLowerCase()}.
        </p>
      )}
      {overview.isPending ? (
        <p>Loading…</p>
      ) : overview.isError ? (
        <p className="error">{errorText(overview.error)}</p>
      ) : (
        <section className="stack">
          <StatusStrip items={statusItems(overview.data.status, (day) => dayLabel(day))} label="System status" />
          <Tiles overview={overview.data} series={series} operator={role !== "analyst"} />
          {trendError !== undefined && <p className="error">{errorText(trendError)}</p>}
          <p className="muted">
            Active users count anyone who used the app, web or Home Assistant that UTC day
            {overview.data.active.day ? `; the latest built day is ${dayLabel(overview.data.active.day)}` : ""}. Trends
            cover the 30 days the rollup last built.
          </p>
          <TrendTable series={series} />
        </section>
      )}
    </>
  );
}

function Tiles({
  overview,
  series,
  operator,
}: {
  overview: AnalyticsOverview;
  series: Record<Trend, AnalyticsSeries | undefined>;
  operator: boolean;
}) {
  const { active, circles, early_access: early } = overview;
  const trend = (metric: Trend) => {
    const data = series[metric];
    if (!data) return undefined;
    return (
      <Sparkline
        label={`${TRENDS[metric]}, ${dayLabel(data.from)} to ${dayLabel(data.to)}`}
        buckets={data.buckets}
        values={totals(data)}
        format={(day, value) => `${dayLabel(day)}: ${value === null ? "not built" : compact(value)}`}
      />
    );
  };
  const change = (metric: Trend) => {
    const data = series[metric];
    const result = data && weekChange(totals(data), data.combine);
    return result ? changeText(result, data.combine === "sum" ? "the 7 days before" : "7 days earlier") : undefined;
  };
  const unbuilt = "Not built yet";
  return (
    <div className="tiles">
      <Tile
        label="Accounts"
        value={compact(overview.users.total)}
        note={`+${compact(overview.users.new_7d)} in the last 7 days${change("signups") ? ` (sign-ups ${change("signups")})` : ""}`}
        trend={trend("signups")}
      />
      <Tile
        label="Daily active"
        value={active.dau === null ? "—" : compact(active.dau)}
        note={active.day ? (change("active") ?? `On ${dayLabel(active.day)}`) : unbuilt}
        trend={trend("active")}
      />
      <Tile
        label="Weekly active"
        value={active.wau === null ? "—" : compact(active.wau)}
        note={active.day ? (change("active_7d") ?? "The 7 days through the latest day") : unbuilt}
        trend={trend("active_7d")}
      />
      <Tile
        label="Monthly active"
        value={active.mau === null ? "—" : compact(active.mau)}
        note={active.day ? (change("active_30d") ?? "The 30 days through the latest day") : unbuilt}
        trend={trend("active_30d")}
      />
      <Tile label="Stickiness" value={percent(active.stickiness)} note="Daily ÷ monthly active" />
      <Tile
        label="Online now"
        value={compact(overview.online_now)}
        note="A live connection or a fix in the last 5 min"
      />
      <Tile
        label="Circles"
        value={compact(circles.total)}
        note={`${compact(circles.family)} family · ${compact(circles.friends)} friends · ${compact(circles.event)} events`}
        trend={trend("circles")}
      />
      <Tile
        label="Early access"
        value={compact(early.confirmed)}
        note={
          <>
            Confirmed; {compact(early.pending)} pending · <Link to="/early-access">details</Link>
          </>
        }
        trend={trend("early_access_signups")}
      />
      <Tile
        label="Open reports"
        value={compact(overview.reports_open)}
        note={operator ? <Link to="/moderation">Moderation queue</Link> : "Awaiting a moderator"}
      />
    </div>
  );
}

function Tile({ label, value, note, trend }: { label: string; value: string; note?: ReactNode; trend?: ReactNode }) {
  return (
    <div className="tile">
      <span className="tile-label">{label}</span>
      <span className="tile-value">{value}</span>
      {note && <span className="tile-note muted">{note}</span>}
      {trend}
    </div>
  );
}

function TrendTable({ series }: { series: Record<Trend, AnalyticsSeries | undefined> }) {
  const buckets = TREND_METRICS.map((metric) => series[metric]?.buckets).find((list) => list !== undefined);
  if (!buckets) return null;
  return (
    <details>
      <summary>Table view</summary>
      <table>
        <thead>
          <tr>
            <th>Day (UTC)</th>
            {TREND_METRICS.map((metric) => (
              <th key={metric} className="num">
                {TRENDS[metric]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {buckets.toReversed().map((day) => (
            <tr key={day}>
              <td>{day}</td>
              {TREND_METRICS.map((metric) => {
                const data = series[metric];
                const value = data ? totals(data)[data.buckets.indexOf(day)] : undefined;
                return (
                  <td key={metric} className="num">
                    {value === null || value === undefined ? "—" : value.toLocaleString()}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}
